// src/utils/mediaPaths.js
export const GCS_PUBLIC_BUCKET =
  process.env.REACT_APP_GCS_PUBLIC_BUCKET || 'staging.togeevision-f3725.appspot.com';

// Allow configurable audio prefix (default: original "audio" folder).
// Also allow a fallback prefix so legacy locations keep working.
const AUDIO_PREFIX_PRIMARY = (
  process.env.REACT_APP_AUDIO_STORAGE_PATH ||
  process.env.REACT_APP_AUDIO_PREFIX ||
  'audio'
).replace(/^\/+/, '').replace(/\/+$/, '');

const AUDIO_PREFIX_FALLBACK = (
  process.env.REACT_APP_AUDIO_STORAGE_PATH_FALLBACK ||
  'audio'
).replace(/^\/+/, '').replace(/\/+$/, '');

const stripLeading = (s='') => s.replace(/^\/+/, '');

export const splitExt = (fname = '') => {
  const i = fname.lastIndexOf('.');
  if (i <= 0) return { base: fname, ext: '' };
  return { base: fname.slice(0, i), ext: fname.slice(i) }; // ext includes dot
};

export const withExt = (base = '', ext = '.mp3') => {
  if (!base) return '';
  return base.endsWith(ext) ? base : `${base}${ext}`;
};

export const toGcsPublicUrl = (bucket = GCS_PUBLIC_BUCKET, objectPath = '') =>
  `https://storage.googleapis.com/${bucket}/${stripLeading(objectPath)}`;

// JSON metadata prefix — mirrors services/backend/utils/gcsChronicleManager.mjs's
// `chroniclesPath`/`keyForId()`, which always writes JSON under this folder.
const CHRONICLE_JSON_PREFIX = (
  process.env.REACT_APP_CHRONICLE_JSON_PREFIX || 'chronicles'
).replace(/^\/+/, '').replace(/\/+$/, '');

// Audio filenames are written as `{date}_{timeOfDay}_chronicle_{isoTimestamp}.mp3` (human-browsable),
// but the JSON sibling backend writes is keyed by the bare id `chronicle_{isoTimestamp}` with no
// date/timeOfDay prefix (see services/backend/utils/chronicleIds.mjs CHRONICLE_MP3_BASENAME_RE) and
// lives under CHRONICLE_JSON_PREFIX, not alongside the audio. Extract that canonical id if present.
const CANONICAL_CHRONICLE_ID_RE = /(chronicle_\d{4}-\d{2}-\d{2}T\d{2}-\d{2}-\d{2}-\d{3}Z)(?:_fr-ca|_fr-qc|_qc|_fr)?$/i;
const canonicalChronicleId = (base) => {
  const m = CANONICAL_CHRONICLE_ID_RE.exec(base);
  return m ? m[1] : null;
};

/** Given a filename that may be bare (no ext) or full (`.mp3`), build the audio & json URLs. */
export const buildChronicleUrls = (name) => {
  // files live under the configured prefix (default 'chronicles/2026/audio')
  const { base, ext } = splitExt(name);
  const mp3Name = ext ? name : `${name}.mp3`;
  const jsonName = `${base}.json`;
  const primaryPath = `${AUDIO_PREFIX_PRIMARY}/${mp3Name}`;
  const fallbackPath = AUDIO_PREFIX_FALLBACK && AUDIO_PREFIX_FALLBACK !== AUDIO_PREFIX_PRIMARY
    ? `${AUDIO_PREFIX_FALLBACK}/${mp3Name}`
    : null;

  const chronicleId = canonicalChronicleId(base);
  const canonicalJsonUrl = chronicleId
    ? toGcsPublicUrl(GCS_PUBLIC_BUCKET, `${CHRONICLE_JSON_PREFIX}/${chronicleId}.json`)
    : null;
  const sameFolderJsonUrl = toGcsPublicUrl(GCS_PUBLIC_BUCKET, `${AUDIO_PREFIX_PRIMARY}/${jsonName}`);

  return {
    mp3Url: toGcsPublicUrl(GCS_PUBLIC_BUCKET, primaryPath),
    // Prefer the canonical chronicles/ path; keep the same-folder guess as a fallback
    // in jsonCandidates only, since it's not where the backend actually writes JSON.
    jsonUrl: canonicalJsonUrl || sameFolderJsonUrl,
    altMp3Url: fallbackPath ? toGcsPublicUrl(GCS_PUBLIC_BUCKET, fallbackPath) : null,
    altJsonUrl: canonicalJsonUrl ? sameFolderJsonUrl : (
      fallbackPath ? toGcsPublicUrl(GCS_PUBLIC_BUCKET, `${AUDIO_PREFIX_FALLBACK}/${jsonName}`) : null
    ),
    filename: mp3Name,     // canonical filename with .mp3
    basename: base,        // mirror key for JSON
  };
};
